/**
 * SettingsManager.js
 * Provides an interface to access the general game settings.
 */

import { Game, DesignDataTypes, CoreEvents } from "pixi_core";

import {
    GameEvents,
    DEFAULT_BUBBLE_SPEED,
    DEFAULT_BUBBLE_PHYSIC_RADIUS,
    DEFAULT_DELAY_BETWEEN_ATTACKS,
    DEFAULT_ENEMY_ATTACK_FLASH_COUNT,
    DEFAULT_ENEMY_ATTACK_FLASH_HIDE_DELAY,
    DEFAULT_ENEMY_ATTACK_FLASH_SHOW_DELAY,
    DEFAULT_ENEMY_ATTACK_SLIDE_DELAY,
    DEFAULT_ENEMY_ATTACK_SLIDE_DISTANCE,
    DEFAULT_SHOW_ABILITY_NAME_DELAY,
    DEFAULT_HERO_DAMAGE_LABEL_SLIDE,
    DEFAULT_HP_PER_SECOND,
    DEFAULT_CUT_IN_MOVE_DURATION,
    DEFAULT_CUT_IN_DELAY,
    DEFAULT_CUT_IN_STOP_DURATION,
    DEFAULT_ALL_OUT_X_DISTANCE,
    DEFAULT_ALL_OUT_Y_DISTANCE,
    DEFAULT_CUT_IN_X_DISTANCE,
    DEFAULT_CUT_IN_Y_DISTANCE,
    DEFAULT_ALL_OUT_DAMAGE_MULTIPLIER,
    DEFAULT_ALL_OUT_DAMAGE_DURATION,
    DEFAULT_DELAY_BETWEEN_MULTIPLE_ACTIONS,
    DEFAULT_DELAY_BETWEEN_MULTIPLE_ABILITIES,
    DEFAULT_RESPAWN_COLUMN_TIME,
    DEFAULT_SECOND_PARTICLE_DELAY,
    DEFAULT_THIRD_PARTICLE_DELAY,
    DEFAULT_FOURTH_PARTICLE_DELAY,
    DEFAULT_PARTICLE_TRAVELING_TIME,
    DEFAULT_PARTICLE_ROTATION,
    DEFAULT_MAX_TIME_TO_POP_ALL_BUBBLES,
    DEFAULT_BUBBLE_POP_TIME_MULTIPLIER,
    DEFAULT_RED_WARNING_LINE_PULSE,
    DEFAULT_MULTICOLOR_BUBBLE_SPIN_TIME,
    DEFAULT_CUT_IN_TEXT_DELAY,
    DEFAULT_CLEAR_DISCONNECTED_DELAY,
    DEFAULT_SCATTER_DELAY,
    DEFAULT_SCATTER_DELAY_MULTIPLIER,
    DEFAULT_SCATTER_DISTANCE,
    DEFAULT_SCATTER_DURATION,
    DEFAULT_SCATTER_DIRECTION_RANDOM_RANGE,
    DEFAULT_SCATTER_DIRECTION_MIN,
    DEFAULT_SCATTER_DIRECTION_MAX,
    DEFAULT_HERO_DAMAGE_LABEL_RANDOM_OFFSET,
    DEFAULT_SCATTER_POP_DELAY,
    DEFAULT_SCATTER_DISTANCE_RANDOM_RANGE,
    DEFAULT_SPAWN_DURATION,
    DEFAULT_SPAWN_CASCADE_DELAY,
    DEFAULT_SPAWN_CASCADE_SPEEDUP,
    DEFAULT_CANNON_RELOAD_TIME,
    DEFAULT_CANNON_RESERVE_SPACING,
    DEFAULT_CANNON_EXTRA_VISIBLE_BUBBLES,
    DEFAULT_PARTICLE_CURVE_MULTIPLIER_1,
    DEFAULT_PARTICLE_CURVE_MULTIPLIER_2,
    DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_DELAY,
    DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_MAX_DELAY,
    DEFAULT_PARTICLE_SCALE,
    DEFAULT_CHAMELEON_BUBBLE_SPIN_TIME,
    DEFAULT_LIGHTNING_SPEED,
    DEFAULT_LIGHTNING_SPEED_RANDOM_RANGE,
    DEFAULT_LIGHTNING_POP_DELAY,
    DEFAULT_LIGHTNING_FADE_DELAY,
    DEFAULT_THOR_ROTATION_SPEED,
    DEFAULT_NEW_WAVE_SLIDE_DISTANCE,
    DEFAULT_NEW_WAVE_SLIDE_TIME,
    DEFAULT_AIMLINE_LENGTH,
    DEFAULT_AIMLINE_REBOUND_LENGTH,
    DEFAULT_ENEMY_DEATH_SLIDE_TIME,
    DEFAULT_HEAL_ANIMATION_DELAY,
    DEFAULT_BLACK_PANTHER_SHIELD_POS_X,
    DEFAULT_BLACK_PANTHER_SHOCKWAVE_POS_X,
    DEFAULT_BLACK_PANTHER_SHOCKWAVE_TIME,
    DEFAULT_BLACK_PANTHER_SHOCKWAVE_POP_TIME,
    DEFAULT_CUT_IN_STOP_DISTANCE_X,
    DEFAULT_CUT_IN_STOP_DISTANCE_Y,
    DEFAULT_CANNON_RANDOM_BUBBLE_COLOR_CHANGE_TIMER,
    DEFAULT_BUFF_TIMER,
    DEFAULT_WEAKNESS_DAMAGE_MULTIPLIER,
    DEFAULT_DAMAGE_FONT_MAX_DAMAGE,
    DEFAULT_DAMAGE_FONT_NORMAL_DAMAGE,
    DEFAULT_DAMAGE_FONT_MAX_SIZE_MULTIPLIER,
    DEFAULT_DAMAGE_FONT_MIN_SIZE_MULTIPLIER,
    DEFAULT_DAMAGE_FONT_NORMAL_SIZE,
    DEFAULT_BASE_DAMAGE,
    DEFAULT_HERO_BASE_DAMAGE,
    DEFAULT_HEAL_BUBBLE_HEALING,
    DEFAULT_MAX_AIM_ANGLE,
    DEFAULT_VICTORY_ANIMATION_DURATION,
    DEFAULT_ENEMY_SHAKE_DURATION,
    DEFAULT_HERO_SHAKE_DURATION,
    DEFAULT_SHAKE_DISTANCE
} from "../Constants";

export class SettingsManager {
    constructor() {
        /** @private @type {Object} */
        this._settings = {};

        /** @private @type {Object} */
        this._enemyAttackPhase = {};

        /** @private @type {Object} */
        this._particleSettings = {};

        /** @public @type {import("../Constants").AnimationSettings} */
        this.animations = {
            clearDisconnectedDelay: DEFAULT_CLEAR_DISCONNECTED_DELAY,
            scatterDelay: DEFAULT_SCATTER_DELAY,
            scatterDelayMultiplier: DEFAULT_SCATTER_DELAY_MULTIPLIER,
            scatterDistance: DEFAULT_SCATTER_DISTANCE,
            scatterDistanceRandomRange: DEFAULT_SCATTER_DISTANCE_RANDOM_RANGE,
            scatterDuration: DEFAULT_SCATTER_DURATION,
            scatterPopDelay: DEFAULT_SCATTER_POP_DELAY,
            scatterDirectionRandomRange: (DEFAULT_SCATTER_DIRECTION_RANDOM_RANGE * Math.PI) / 180,
            scatterDirectionMin: (DEFAULT_SCATTER_DIRECTION_MIN * Math.PI) / 180,
            scatterDirectionMax: (DEFAULT_SCATTER_DIRECTION_MAX * Math.PI) / 180,
            spawnDuration: DEFAULT_SPAWN_DURATION,
            spawnCascadeDelay: DEFAULT_SPAWN_CASCADE_DELAY,
            spawnCascadeSpeedUp: DEFAULT_SPAWN_CASCADE_SPEEDUP / 100,
            healDelay: DEFAULT_HEAL_ANIMATION_DELAY,
            buffTimer: DEFAULT_BUFF_TIMER
        };

        /** @public @type {import("../Constants").BubbleSettings} */
        this.bubbles = {
            lightningSpeed: DEFAULT_LIGHTNING_SPEED,
            lightningSpeedRandomRange: DEFAULT_LIGHTNING_SPEED_RANDOM_RANGE,
            lightningPopDelay: DEFAULT_LIGHTNING_POP_DELAY,
            lightningFadeDelay: DEFAULT_LIGHTNING_FADE_DELAY,
            thorRotationSpeed: DEFAULT_THOR_ROTATION_SPEED
        };

        /** @public @type {import("../Constants").AimLineSettings} */
        this.aimLine = {
            length: DEFAULT_AIMLINE_LENGTH,
            reboundLength: DEFAULT_AIMLINE_REBOUND_LENGTH
        };

        /** @public @type {import("../Constants").AudioSettings} */
        this.audio = {
            bubbleBurstOverlapDelay: DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_DELAY,
            bubbleBurstOverlapMaxDelay: DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_MAX_DELAY
        };

        /** @public @type {import("../Constants").SpaceManipulationSettings} */
        this.spaceManipulation = {
            probRegular: 1,
            probHeal: 1,
            probShield: 1,
            probChameleon: 1,
            probColorless: 1,
            probStone: 1,
            probBlooming: 1,
            probStoneBlooming: 1
        };

        Game.on(CoreEvents.eDESIGNDATAMANAGER_READY, this._onDesignDataReady, this);
    }

    /**
     * @private
     */
    _onDesignDataReady() {
        const general = Game.design.getData("General", DesignDataTypes.eGLOBAL_ENTITY);

        const settings = Game.design.findEntityByName(general, "Settings");
        const enemyAttackPhase = Game.design.findEntityByName(general, "EnemyAttackPhase");
        const particleSettings = Game.design.findEntityByName(general, "ParticleSettings");
        const animationSettings = Game.design.findEntityByName(general, "Animations");
        const audioSettings = Game.design.findEntityByName(general, "Audio");
        const bubbleSettings = Game.design.findEntityByName(general, "BubbleAnimations");
        const spaceManipulationSettings = Game.design.findEntityByName(general, "SpaceManipulation");

        let settingsCopy = JSON.parse(JSON.stringify(settings));
        let enemyAttackPhaseCopy = JSON.parse(JSON.stringify(enemyAttackPhase));
        let particleSettingsCopy = JSON.parse(JSON.stringify(particleSettings));

        // Remove level editor entity properties not useful in the game
        delete settingsCopy.id;
        delete settingsCopy.type;
        delete settingsCopy.x;
        delete settingsCopy.y;
        delete settingsCopy.children;
        delete settingsCopy.allowChildren;

        delete enemyAttackPhaseCopy.id;
        delete enemyAttackPhaseCopy.type;
        delete enemyAttackPhaseCopy.x;
        delete enemyAttackPhaseCopy.y;
        delete enemyAttackPhaseCopy.children;
        delete enemyAttackPhaseCopy.allowChildren;

        delete particleSettingsCopy.id;
        delete particleSettingsCopy.type;
        delete particleSettingsCopy.x;
        delete particleSettingsCopy.y;
        delete particleSettingsCopy.children;
        delete particleSettingsCopy.allowChildren;

        this._settings = settingsCopy;
        this._enemyAttackPhase = enemyAttackPhaseCopy;
        this._particleSettings = particleSettingsCopy;

        // Animation settings
        this.animations.clearDisconnectedDelay = Game.utils.assertNumber(
            animationSettings.ClearDisconnectedDelay,
            DEFAULT_CLEAR_DISCONNECTED_DELAY
        );

        // Disconnected/dropped bubble scatter animation settings
        this.animations.scatterDelayMultiplier = Game.utils.assertNumber(
            animationSettings.ScatterDelayMultiplier,
            DEFAULT_SCATTER_DELAY_MULTIPLIER
        );
        this.animations.scatterDistance = Game.utils.assertNumber(animationSettings.ScatterDistance, DEFAULT_SCATTER_DISTANCE);
        this.animations.scatterDistanceRandomRange = Game.utils.assertNumber(
            animationSettings.ScatterDistanceRandomRange,
            DEFAULT_SCATTER_DISTANCE_RANDOM_RANGE
        );

        this.animations.scatterDuration = Game.utils.assertNumber(animationSettings.ScatterDuration, DEFAULT_SCATTER_DURATION);
        this.animations.scatterPopDelay = Game.utils.assertNumber(animationSettings.ScatterPopDelay, DEFAULT_SCATTER_POP_DELAY);

        this.animations.scatterDirectionMin = Game.utils.assertNumber(animationSettings.ScatterDirectionMin, DEFAULT_SCATTER_DIRECTION_MIN);
        this.animations.scatterDirectionMax = Game.utils.assertNumber(animationSettings.ScatterDirectionMax, DEFAULT_SCATTER_DIRECTION_MAX);
        this.animations.scatterDirectionRandomRange = Game.utils.assertNumber(
            animationSettings.ScatterDirectionRandomRange,
            DEFAULT_SCATTER_DIRECTION_RANDOM_RANGE
        );

        this.animations.healDelay = Game.utils.assertNumber(animationSettings.HealDelay, DEFAULT_HEAL_ANIMATION_DELAY);
        this.animations.buffTimer = Game.utils.assertNumber(animationSettings.BuffTimer, DEFAULT_BUFF_TIMER);

        // Convert degrees to radians
        this.animations.scatterDirectionMin *= Math.PI / 180;
        this.animations.scatterDirectionMax *= Math.PI / 180;
        this.animations.scatterDirectionRandomRange *= Math.PI / 180;

        // Spawn bubble animation settings
        this.animations.spawnDuration = Game.utils.assertNumber(animationSettings.SpawnDuration, DEFAULT_SPAWN_DURATION);
        this.animations.spawnCascadeDelay = Game.utils.assertNumber(animationSettings.SpawnCascadeDelay, DEFAULT_SPAWN_CASCADE_DELAY);
        this.animations.spawnCascadeSpeedUp =
            Game.utils.assertNumber(animationSettings.SpawnCascadeSpeedUp, DEFAULT_SPAWN_CASCADE_SPEEDUP) / 100;

        // Bubble animation settings
        this.bubbles.lightningSpeed = Game.utils.assertNumber(bubbleSettings.LightningSpeed, DEFAULT_LIGHTNING_SPEED);
        this.bubbles.lightningSpeedRandomRange = Game.utils.assertNumber(
            bubbleSettings.LightningSpeedRandomRange,
            DEFAULT_LIGHTNING_SPEED_RANDOM_RANGE
        );
        this.bubbles.lightningPopDelay = Game.utils.assertNumber(bubbleSettings.LightningPopDelay, DEFAULT_LIGHTNING_POP_DELAY);
        this.bubbles.lightningFadeDelay = Game.utils.assertNumber(bubbleSettings.LightningFadeDelay, DEFAULT_LIGHTNING_FADE_DELAY);
        this.bubbles.thorRotationSpeed = Game.utils.assertNumber(bubbleSettings.ThorRotationSpeed, DEFAULT_THOR_ROTATION_SPEED);

        // Aim line
        this.aimLine.length = Game.utils.assertNumber(this._settings.AimLineLength, DEFAULT_AIMLINE_LENGTH);
        this.aimLine.reboundLength = Game.utils.assertNumber(this._settings.AimLineReboundLength, DEFAULT_AIMLINE_REBOUND_LENGTH);

        // Audio settings
        this.audio.bubbleBurstOverlapDelay = Game.utils.assertNumber(
            audioSettings.BubbleBurstOverlapDelay,
            DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_DELAY
        );
        this.audio.bubbleBurstOverlapMaxDelay = Game.utils.assertNumber(
            audioSettings.BubbleBurstOverlapMaxDelay,
            DEFAULT_AUDIO_BUBBLE_BURST_OVERLAP_MAX_DELAY
        );

        // Space manipulation settings
        this.spaceManipulation.probRegular = Game.utils.assertNumber(spaceManipulationSettings.ProbRegular, 1);
        this.spaceManipulation.probHeal = Game.utils.assertNumber(spaceManipulationSettings.ProbHeal, 1);
        this.spaceManipulation.probShield = Game.utils.assertNumber(spaceManipulationSettings.ProbShield, 1);
        this.spaceManipulation.probChameleon = Game.utils.assertNumber(spaceManipulationSettings.ProbChameleon, 1);
        this.spaceManipulation.probColorless = Game.utils.assertNumber(spaceManipulationSettings.ProbColorless, 1);
        this.spaceManipulation.probStone = Game.utils.assertNumber(spaceManipulationSettings.ProbStone, 1);
        this.spaceManipulation.probBlooming = Game.utils.assertNumber(spaceManipulationSettings.ProbBlooming, 1);
        this.spaceManipulation.probStoneBlooming = Game.utils.assertNumber(spaceManipulationSettings.ProbStoneBlooming, 1);

        Game.fire(GameEvents.eSETTINGSMANAGER_READY, this);
    }

    /**
     * @public
     * @returns {Number}
     */
    get baseDamage() {
        return Game.utils.assertNumber(this._settings.BaseDamage, DEFAULT_BASE_DAMAGE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get heroBaseDamage() {
        return Game.utils.assertNumber(this._settings.HeroBaseDamage, DEFAULT_HERO_BASE_DAMAGE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get healBubbleHealing() {
        return Game.utils.assertNumber(this._settings.HealBubbleHealing, DEFAULT_HEAL_BUBBLE_HEALING);
    }

    /**
     * @public
     * @returns {Number}
     */
    get bubbleSpeed() {
        return Game.utils.assertNumber(this._settings.BubbleSpeed, DEFAULT_BUBBLE_SPEED);
    }

    /**
     * @public
     * @returns {Number}
     */
    get collisionRadius() {
        return Game.utils.assertNumber(this._settings.CollisionRadius, DEFAULT_BUBBLE_PHYSIC_RADIUS);
    }

    /**
     * @public
     * @returns {Number}
     */
    get maxAimAngle() {
        return Game.utils.assertNumber(this._settings.MaxAimAngle, DEFAULT_MAX_AIM_ANGLE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get victoryAnimationDuration() {
        return Game.utils.assertNumber(this._settings.VictoryAnimationDuration, DEFAULT_VICTORY_ANIMATION_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get enemyShakeDuration() {
        return Game.utils.assertNumber(this._settings.EnemyShakeDuration, DEFAULT_ENEMY_SHAKE_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get heroShakeDuration() {
        return Game.utils.assertNumber(this._settings.HeroShakeDuration, DEFAULT_HERO_SHAKE_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get shakeDistance() {
        return Game.utils.assertNumber(this._settings.ShakeDistance, DEFAULT_SHAKE_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseFlashCount() {
        return Game.utils.assertNumber(this._enemyAttackPhase.FlashCount, DEFAULT_ENEMY_ATTACK_FLASH_COUNT);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseFlashHideDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.FlashHideDelay, DEFAULT_ENEMY_ATTACK_FLASH_HIDE_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseFlashShowDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.FlashShowDelay, DEFAULT_ENEMY_ATTACK_FLASH_SHOW_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseSlideDistance() {
        return Game.utils.assertNumber(this._enemyAttackPhase.SlideDistance, DEFAULT_ENEMY_ATTACK_SLIDE_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseSlideDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.SlideDelay, DEFAULT_ENEMY_ATTACK_SLIDE_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseDelayBetweenAttacks() {
        return Game.utils.assertNumber(this._enemyAttackPhase.DelayBetweenAttacks, DEFAULT_DELAY_BETWEEN_ATTACKS);
    }

    /**
     * @public
     * @returns {Number}
     */
    get attackPhaseAbilityNameDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.AbilityNameDelay, DEFAULT_SHOW_ABILITY_NAME_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get heroDamageSlideDistance() {
        return Game.utils.assertNumber(this._settings.HeroDamageSlideDistance, DEFAULT_HERO_DAMAGE_LABEL_SLIDE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get heroDamageLabelRandomOffset() {
        return Game.utils.assertNumber(this._settings.HeroDamageLabelRandomOffset, DEFAULT_HERO_DAMAGE_LABEL_RANDOM_OFFSET);
    }

    /**
     * @public
     * @returns {Number}
     */
    get hpPerSecond() {
        return Game.utils.assertNumber(this._settings.HPperSecond, DEFAULT_HP_PER_SECOND);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInMoveDuration() {
        return Game.utils.assertNumber(this._settings.CutInMoveDuration, DEFAULT_CUT_IN_TEXT_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInStopDuration() {
        return Game.utils.assertNumber(this._settings.CutInStopDuration, DEFAULT_CUT_IN_STOP_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInStopDistanceX() {
        return Game.utils.assertNumber(this._settings.CutInStopDistanceX, DEFAULT_CUT_IN_STOP_DISTANCE_X);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInStopDistanceY() {
        return Game.utils.assertNumber(this._settings.CutInStopDistanceY, DEFAULT_CUT_IN_STOP_DISTANCE_Y);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInTextDelay() {
        return Game.utils.assertNumber(this._settings.CutInTextDelay, DEFAULT_CUT_IN_MOVE_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInDelay() {
        return Game.utils.assertNumber(this._settings.CutInDelay, DEFAULT_CUT_IN_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInXDistance() {
        return Game.utils.assertNumber(this._settings.HeroCutInXDistance, DEFAULT_CUT_IN_X_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cutInYDistance() {
        return Game.utils.assertNumber(this._settings.HeroCutInYDistance, DEFAULT_CUT_IN_Y_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get allOutInXDistance() {
        return Game.utils.assertNumber(this._settings.AllOutCutInXDistance, DEFAULT_ALL_OUT_X_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get allOutInYDistance() {
        return Game.utils.assertNumber(this._settings.AllOutCutInYDistance, DEFAULT_ALL_OUT_Y_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get allOutDamageMultiplier() {
        return Game.utils.assertNumber(this._settings.allOutDamageMultiplier, DEFAULT_ALL_OUT_DAMAGE_MULTIPLIER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get allOutDamageDuration() {
        return Game.utils.assertNumber(this._settings.AllOutDamageDuration, DEFAULT_ALL_OUT_DAMAGE_DURATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get multipleActionDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.MultipleActionDelay, DEFAULT_DELAY_BETWEEN_MULTIPLE_ACTIONS);
    }

    /**
     * @public
     * @returns {Number}
     */
    get multipleAbilityDelay() {
        return Game.utils.assertNumber(this._enemyAttackPhase.MultipleAbilityDelay, DEFAULT_DELAY_BETWEEN_MULTIPLE_ABILITIES);
    }

    /**
     * @public
     * @returns {Number}
     */
    get newWaveSlideDistance() {
        return Game.utils.assertNumber(this._enemyAttackPhase.NewWaveSlideDistance, DEFAULT_NEW_WAVE_SLIDE_DISTANCE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get newWaveSlideTime() {
        return Game.utils.assertNumber(this._enemyAttackPhase.NewWaveSlideTime, DEFAULT_NEW_WAVE_SLIDE_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get enemyDeathSlideTime() {
        return Game.utils.assertNumber(this._enemyAttackPhase.EnemyDeathSlideTime, DEFAULT_ENEMY_DEATH_SLIDE_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get respawnColumnTime() {
        return Game.utils.assertNumber(this._settings.RespawnColumnTime, DEFAULT_RESPAWN_COLUMN_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get bubblePopTimeMultiplier() {
        return Game.utils.assertNumber(this._settings.BubblePopTimeMultiplier, DEFAULT_BUBBLE_POP_TIME_MULTIPLIER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get redWarningLinePulse() {
        return Game.utils.assertNumber(this._settings.RedWarningLinePulse, DEFAULT_RED_WARNING_LINE_PULSE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get multicolorBubbleSpinTime() {
        return Game.utils.assertNumber(this._settings.MulticolorBubbleSpinTime, DEFAULT_MULTICOLOR_BUBBLE_SPIN_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get chameleonBubbleSpinTime() {
        return Game.utils.assertNumber(this._settings.ChameleonBubbleSpinTime, DEFAULT_CHAMELEON_BUBBLE_SPIN_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cannonReloadTime() {
        return Game.utils.assertNumber(this._settings.CannonReloadTime, DEFAULT_CANNON_RELOAD_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cannonReserveSpacing() {
        return Game.utils.assertNumber(this._settings.CannonReserveSpacing, DEFAULT_CANNON_RESERVE_SPACING);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cannonExtraVisibleBubbles() {
        return Game.utils.assertNumber(this._settings.CannonExtraVisibleBubbles, DEFAULT_CANNON_EXTRA_VISIBLE_BUBBLES);
    }

    /**
     * @public
     * @returns {Number}
     */
    get cannonRandomBubbleColorChangeTimer() {
        return Game.utils.assertNumber(this._settings.CannonRandomBubbleColorChangeTimer, DEFAULT_CANNON_RANDOM_BUBBLE_COLOR_CHANGE_TIMER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get blackPantherShieldPosX() {
        return Game.utils.assertNumber(this._settings.BlackPantherShieldPosX, DEFAULT_BLACK_PANTHER_SHIELD_POS_X);
    }

    /**
     * @public
     * @returns {Number}
     */
    get blackPantherShockwavePosX() {
        return Game.utils.assertNumber(this._settings.BlackPantherShockwavePosX, DEFAULT_BLACK_PANTHER_SHOCKWAVE_POS_X);
    }

    /**
     * @public
     * @returns {Number}
     */
    get blackPantherShockwaveTime() {
        return Game.utils.assertNumber(this._settings.BlackPantherShockwaveTime, DEFAULT_BLACK_PANTHER_SHOCKWAVE_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get blackPantherShockwavePopTime() {
        return Game.utils.assertNumber(this._settings.BlackPantherShockwavePopTime, DEFAULT_BLACK_PANTHER_SHOCKWAVE_POP_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get damageFontMaxDamage() {
        return Game.utils.assertNumber(this._settings.DamageFontMaxDamage, DEFAULT_DAMAGE_FONT_MAX_DAMAGE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get damageFontNormalDamage() {
        return Game.utils.assertNumber(this._settings.DamageFontNormalDamage, DEFAULT_DAMAGE_FONT_NORMAL_DAMAGE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get damageFontNormalSize() {
        return Game.utils.assertNumber(this._settings.DamageFontNormalSize, DEFAULT_DAMAGE_FONT_NORMAL_SIZE);
    }

    /**
     * @public
     * @returns {Number}
     */
    get damageFontMaxSizeMultiplier() {
        return Game.utils.assertNumber(this._settings.DamageFontMaxSizeMultiplier, DEFAULT_DAMAGE_FONT_MAX_SIZE_MULTIPLIER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get damageFontMinSizeMultiplier() {
        return Game.utils.assertNumber(this._settings.DamageFontMinSizeMultiplier, DEFAULT_DAMAGE_FONT_MIN_SIZE_MULTIPLIER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get weaknessDamageMultiplier() {
        return Game.utils.assertNumber(this._settings.WeaknessDamageMultiplier, DEFAULT_WEAKNESS_DAMAGE_MULTIPLIER);
    }

    /**
     * @public
     * @returns {Number}
     */
    get secondParticleDelay() {
        return Game.utils.assertNumber(this._particleSettings.SecondParticleDelay, DEFAULT_SECOND_PARTICLE_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get thirdParticleDelay() {
        return Game.utils.assertNumber(this._particleSettings.ThirdParticleDelay, DEFAULT_THIRD_PARTICLE_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get fourthParticleDelay() {
        return Game.utils.assertNumber(this._particleSettings.FourthParticleDelay, DEFAULT_FOURTH_PARTICLE_DELAY);
    }

    /**
     * @public
     * @returns {Number}
     */
    get particleTravelingTime() {
        return Game.utils.assertNumber(this._particleSettings.ParticleTravelingTime, DEFAULT_PARTICLE_TRAVELING_TIME);
    }

    /**
     * @public
     * @returns {Number}
     */
    get particleRotation() {
        return Game.utils.assertNumber(this._particleSettings.ParticleRotation, DEFAULT_PARTICLE_ROTATION);
    }

    /**
     * @public
     * @returns {Number}
     */
    get maxTimeToPopAllBubbles() {
        return Game.utils.assertNumber(this._particleSettings.MaxTimeToPopAllBubbles, DEFAULT_MAX_TIME_TO_POP_ALL_BUBBLES);
    }

    /**
     * @public
     * @returns {Number}
     */
    get particleCurveMultiplier1() {
        return Game.utils.assertNumber(this._particleSettings.ParticleCurveMultiplier1, DEFAULT_PARTICLE_CURVE_MULTIPLIER_1);
    }

    /**
     * @public
     * @returns {Number}
     */
    get particleCurveMultiplier2() {
        return Game.utils.assertNumber(this._particleSettings.ParticleCurveMultiplier2, DEFAULT_PARTICLE_CURVE_MULTIPLIER_2);
    }

    /**
     * @public
     * @returns {Number}
     */
    get particleScale() {
        return Game.utils.assertNumber(this._particleSettings.ParticleScale, DEFAULT_PARTICLE_SCALE);
    }
}
